import { useHandler } from '../handlers/useHandler';
import { useCallback } from 'react';
import { usePipeline } from 'crm-object-board-core/pipeline';
import { useObjectTypeDefinition } from 'crm-object-board-core/objectTypeDefinition';
import { useLocallyUpdateProperties } from '../reconciliation/useLocallyUpdateProperties';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { useRunStageTransitionValidation } from './governance/useRunStageTransitionValidation';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import { useLogger } from 'crm-object-board-core/logger';
import { useOpenConditionalPropertiesPanel } from './conditionalProperties/useOpenConditionalPropertiesPanel';
import { useUpdateCrmObject } from './useUpdateCrmObject';
import { useLoadStageSettings } from '../stageSettings/hooks/useLoadStageSettings';
import { loadCSATSurvey } from '../utils/feedback';
import { useHasCardV2 } from 'crm-object-board-core/hooks';
import { jsx as _jsx } from "react/jsx-runtime";
const MOVE_TO_APPROVALSTAGE_SURVEY_ID_PROD = 1217;
const MOVE_TO_APPROVALSTAGE_SURVEY_ID_QA = 463;
const MOVE_STAGE_SURVEY_ID_PROD_CARDV2 = 1280;
const MOVE_STAGE_SURVEY_ID_QA_CARDV2 = 492;
export const useHandleColumnChange = () => {
  const pipeline = usePipeline();
  useLoadStageSettings();
  const hasCardV2 = useHasCardV2();
  const logger = useLogger();
  const typeDef = useObjectTypeDefinition();
  const locallyUpdateProperties = useLocallyUpdateProperties();
  const runStageTransitionValidation = useRunStageTransitionValidation();
  const updateCrmObject = useUpdateCrmObject();
  const openConditionalPropertiesPanel = useOpenConditionalPropertiesPanel(typeDef === null || typeDef === void 0 ? void 0 : typeDef.pipelineStagePropertyName);
  const getStageMutation = useCallback(stageId => {
    if (!typeDef) {
      return {};
    }
    return {
      [typeDef.pipelineStagePropertyName]: stageId
    };
  }, [typeDef]);
  const findStage = useCallback(stageId => {
    if (!pipeline) {
      return undefined;
    }
    return pipeline.stages.find(s => s.stageId === stageId);
  }, [pipeline]);
  const onColumnChangePromise = useCallback(async ({
    source,
    destination,
    onFinishUpdateCallback,
    itemId
  }) => {
    // We only want to fire a drop event if the data came from a different column
    if (source.droppableId === destination.droppableId || !itemId || !pipeline) {
      onFinishUpdateCallback();
      return;
    }
    const sourceStage = findStage(source.droppableId);
    const destinationStage = findStage(destination.droppableId);
    if (!sourceStage || !destinationStage || !typeDef) {
      onFinishUpdateCallback();
      return;
    }

    //locally update to new collumn
    locallyUpdateProperties(itemId, getStageMutation(destinationStage.stageId));

    //prepare rollback function
    const rollbackColumnChange = () => locallyUpdateProperties(itemId, getStageMutation(sourceStage.stageId));
    const cancelColumnChange = () => {
      rollbackColumnChange();
      onFinishUpdateCallback();
    };

    //any errors we display an alert and rollback
    try {
      //move set to true or false via validation
      const valid = await runStageTransitionValidation({
        objectId: itemId,
        destinationStageId: destinationStage.stageId
      });
      if (!valid) {
        return cancelColumnChange();
      }
      //prepare update
      let updates = getStageMutation(destinationStage.stageId);

      //await cpp result
      //returns either false or the updates to apply
      const cppResult = await openConditionalPropertiesPanel({
        columnId: destinationStage.stageId,
        originalColumnId: sourceStage.stageId,
        objectId: itemId
      });
      //was there updates
      //we default to having 1 change (changing column)
      //if we get more than 1 then CPP was used.
      const cppNeeded = Object.keys(cppResult).length > 1;
      //cpp was cancelled
      //we cancel the move
      if (!cppResult) {
        return cancelColumnChange();
      } else {
        //otherwise update to the new updates
        //this includes stage
        updates = cppResult;
      }

      //allowed to move and has updates
      const objChanges = await updateCrmObject({
        objectId: itemId,
        properties: updates
      });
      //if new changes came back from api
      //update updates to have them
      //so they will be reflected locally
      if (objChanges) {
        Object.entries(objChanges.properties).forEach(([key, value]) => {
          const val = value;
          updates[key] = val.value;
        });
      }

      //apply updates locally
      //this updates the UI for any CPP changes
      locallyUpdateProperties(itemId, updates);
      if (destinationStage.isApprovalStage) {
        loadCSATSurvey({
          qaId: MOVE_TO_APPROVALSTAGE_SURVEY_ID_QA,
          prodId: MOVE_TO_APPROVALSTAGE_SURVEY_ID_PROD
        });
      } else if (hasCardV2) {
        loadCSATSurvey({
          qaId: MOVE_STAGE_SURVEY_ID_QA_CARDV2,
          prodId: MOVE_STAGE_SURVEY_ID_PROD_CARDV2
        });
      }

      //perform log
      //taken from CPP
      logger.log({
        key: 'columnChanged',
        metadata: {
          nextStageId: destinationStage.stageId,
          objectId: itemId,
          conditionalProperties: !!cppNeeded
        }
      });
    } catch (_unused) {
      //error roll back and show alert
      rollbackColumnChange();
      FloatingAlertStore.addAlert({
        type: 'danger',
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "crmObjectBoard.governance.changeStage.error"
        }),
        message: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "crmObjectBoard.error.message"
        })
      });
    }
    //finally call finish update to allow dnd again
    onFinishUpdateCallback();
  }, [pipeline, runStageTransitionValidation, updateCrmObject, typeDef, locallyUpdateProperties, openConditionalPropertiesPanel, logger, getStageMutation, findStage, hasCardV2]);
  const onColumnChange = useCallback(args => {
    Promise.resolve(onColumnChangePromise(args)).catch(console.error);
  }, [onColumnChangePromise]);
  useHandler('onColumnChange', onColumnChange);
};